/**
 * Query-param helpers for shareable filters on Content / Blog / Search pages.
 * Shop browse filters live in the path (`websitePublicTemplatePath.ts`);
 * these binary/enum filters use `?…` so they compose with path segments.
 */

export type ContentShoppableFilter = "all" | "yes";
export type SearchScopeParam = "all" | "products" | "content" | "shoppable";

export function readContentShoppableFromSearch(
  search: string | undefined | null,
): ContentShoppableFilter {
  const sp = new URLSearchParams(search || "");
  return sp.get("shoppable") === "yes" ? "yes" : "all";
}

/** Editor / shareable query mirror of content platform + format browse. */
export function readContentBrowseFromSearch(
  search: string | undefined | null,
): { primarySlug: string | null; secondarySlug: string | null } {
  const sp = new URLSearchParams(search || "");
  const platform = (sp.get("platform") || "").trim();
  const format = (sp.get("format") || "").trim();
  return {
    primarySlug: platform || null,
    secondarySlug: format || null,
  };
}

export function readBlogCategoryFromSearch(
  search: string | undefined | null,
): string {
  return (new URLSearchParams(search || "").get("category") || "").trim();
}

export function readSearchScopeFromSearch(
  search: string | undefined | null,
): SearchScopeParam {
  const raw = (new URLSearchParams(search || "").get("scope") || "")
    .trim()
    .toLowerCase();
  // `linked` kept as a legacy alias for older shared URLs.
  if (raw === "products" || raw === "content" || raw === "shoppable") return raw;
  if (raw === "linked") return "shoppable";
  return "all";
}

/** Keep page-specific filter query keys when rewriting the path. */
export function preservedFilterSearchForPage(
  pageKey: string,
  currentSearch: string | undefined | null,
): string {
  const sp = new URLSearchParams(currentSearch || "");
  const next = new URLSearchParams();

  // Attribution param — keep across page changes so analytics `source` survives SPA nav.
  const source = (sp.get("source") || "").trim();
  if (source) next.set("source", source);

  if (pageKey === "content") {
    if (sp.get("shoppable") === "yes") next.set("shoppable", "yes");
    const platform = (sp.get("platform") || "").trim();
    const format = (sp.get("format") || "").trim();
    if (platform) next.set("platform", platform);
    if (format) next.set("format", format);
  } else if (pageKey === "journal" || pageKey === "journalPost") {
    const cat = (sp.get("category") || "").trim();
    if (cat) next.set("category", cat);
  } else if (pageKey === "search") {
    const term = (sp.get("term") || "").trim();
    const from = (sp.get("from") || "").trim();
    const scope = readSearchScopeFromSearch(currentSearch);
    if (term) {
      next.set("term", term);
      if (from) next.set("from", from);
    }
    if (scope !== "all") next.set("scope", scope);
  }

  const qs = next.toString();
  return qs ? `?${qs}` : "";
}

export function patchSearchParam(
  currentSearch: string | undefined | null,
  key: string,
  value: string | null,
): string {
  const sp = new URLSearchParams(currentSearch || "");
  if (value == null || value === "") sp.delete(key);
  else sp.set(key, value);
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}
